import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import ERPLayout from "@/components/feature/ERPLayout";
import { employeesApi, deliverablesApi } from "@/services/api";
import { Employee } from "@/pages/employees/page";

interface DeliverableFile {
  id: number;
  jobNo: string;
  fileName: string;
  fileType: string;
  category: string;
  version: string;
  status: string;
  size: string;
  uploadedBy: string;
  uploadedAt: string;
  downloadCount: number;
  isLatest: boolean;
  jobTitle?: string;
  jobId?: number;
  description?: string;
}

const deptColors: Record<string, { bg: string; text: string }> = {
  "Field Operations": { bg: "#fef3c7", text: "#92400e" },
  "Office":           { bg: "#fffbeb", text: "#b45309" },
  "Management":       { bg: "#fef9c3", text: "#854d0e" },
  "Lab":              { bg: "#fef3c7", text: "#78350f" },
};

const statusColors: Record<string, { bg: string; text: string }> = {
  "Active":    { bg: "#ecfdf5", text: "#16a34a" },
  "On Leave":  { bg: "#fffbeb", text: "#d97706" },
  "Inactive":  { bg: "#f3f4f6", text: "#6b7280" },
};

const jobStatusColors: Record<string, string> = {
  "Completed": "bg-amber-100 text-amber-700",
  "Delivered": "bg-gray-800 text-white",
  "In Progress": "bg-amber-100 text-amber-700",
  "Pending": "bg-gray-100 text-gray-600",
};

const monthlyUtilization: Record<number, number[]> = {
  1: [72, 80, 85, 90, 78, 85],
  2: [60, 65, 70, 72, 68, 72],
  3: [80, 88, 92, 90, 85, 90],
  4: [40, 50, 55, 55, 52, 55],
  5: [35, 38, 42, 40, 38, 40],
  6: [88, 92, 95, 98, 94, 95],
  7: [75, 80, 85, 88, 84, 88],
  8: [50, 55, 60, 62, 58, 60],
  9: [65, 70, 75, 78, 74, 78],
  10: [38, 42, 45, 45, 43, 45],
};

const months = ["Nov", "Dec", "Jan", "Feb", "Mar", "Apr"];

function UtilizationChart({ empId, current }: { empId: string | number; current: number }) {
  const data = monthlyUtilization[empId] || [50, 55, 60, 65, 62, current];
  const max = 100;
  const chartH = 120;
  const chartW = 400;
  const barW = 40;
  const gap = (chartW - data.length * barW) / (data.length + 1);

  return (
    <div className="w-full overflow-x-auto">
      <svg viewBox={`0 0 ${chartW} ${chartH + 40}`} className="w-full" style={{ minWidth: 320 }}>
        {[0, 25, 50, 75, 100].map((v) => {
          const y = chartH - (v / max) * chartH;
          return (
            <g key={v}>
              <line x1={0} y1={y} x2={chartW} y2={y} stroke="#f3f4f6" strokeWidth={1} />
              <text x={chartW - 2} y={y + 4} textAnchor="end" fontSize={9} fill="#9ca3af">{v}%</text>
            </g>
          );
        })}
        {data.map((val, i) => {
          const x = gap + i * (barW + gap);
          const barH = (val / max) * chartH;
          const y = chartH - barH;
          const isLast = i === data.length - 1;
          const color = val >= 80 ? "#ef4444" : val >= 60 ? "#f59e0b" : "#10b981";
          return (
            <g key={i}>
              <rect x={x} y={y} width={barW} height={barH} rx={4} fill={isLast ? color : `${color}80`} />
              <text x={x + barW / 2} y={y - 4} textAnchor="middle" fontSize={9} fill={color} fontWeight={isLast ? "700" : "400"}>
                {val}%
              </text>
              <text x={x + barW / 2} y={chartH + 14} textAnchor="middle" fontSize={9} fill={isLast ? "#374151" : "#9ca3af"} fontWeight={isLast ? "600" : "400"}>
                {months[i]}
              </text>
            </g>
          );
        })}
        <polyline
          points={data.map((val, i) => {
            const x = gap + i * (barW + gap) + barW / 2;
            const y = chartH - (val / max) * chartH;
            return `${x},${y}`;
          }).join(" ")}
          fill="none"
          stroke="#6366f1"
          strokeWidth={1.5}
          strokeDasharray="4 2"
          opacity={0.6}
        />
        {data.map((val, i) => {
          const x = gap + i * (barW + gap) + barW / 2;
          const y = chartH - (val / max) * chartH;
          return <circle key={i} cx={x} cy={y} r={3} fill="#6366f1" opacity={0.7} />;
        })}
      </svg>
    </div>
  );
}

function StatCard({ label, value, icon, color, bg }: { label: string; value: string | number; icon: string; color: string; bg: string }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4">
      <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-3" style={{ background: bg }}>
        <i className={`${icon} text-base`} style={{ color }} />
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-xs text-gray-500 mt-0.5">{label}</p>
    </div>
  );
}

interface JobHistory {
  empId: number;
  jobNo: string;
  title: string;
  role: string;
  status: string;
  from: string;
  to: string;
}

export default function EmployeeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"overview" | "jobs" | "deliverables">("overview");
  const [emp, setEmp] = useState<Employee | null>(null);
  const [history, setHistory] = useState<JobHistory[]>([]);
  const [empDeliverables, setEmpDeliverables] = useState<DeliverableFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState<Partial<Employee>>({});
  const [editPassword, setEditPassword] = useState("");
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [editSaveMsg, setEditSaveMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);

  const ROLE_OPTIONS = [
    "Survey Manager",
    "Field Surveyor",
    "Senior Surveyor",
    "CAD Technician",
    "GIS Analyst",
    "Drone Operator",
    "Lab Technician",
    "Project Coordinator",
    "Office Administrator",
    "Accounts Executive",
    "HR Manager",
    "Director",
    "Managing Director",
  ];

  function getPasswordStrength(pw: string): { label: string; color: string; width: string } {
    if (!pw) return { label: "", color: "", width: "0%" };
    let score = 0;
    if (pw.length >= 8) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    if (score <= 1) return { label: "Weak", color: "#ef4444", width: "25%" };
    if (score === 2) return { label: "Fair", color: "#f59e0b", width: "50%" };
    if (score === 3) return { label: "Good", color: "#10b981", width: "75%" };
    return { label: "Strong", color: "#059669", width: "100%" };
  }

  useEffect(() => {
    const fetchEmployee = async () => {
      try {
        const result = await employeesApi.getById(id || '');
        const empData = result?.data || result;
        if (empData) {
          setEmp(empData);
          setEditForm(empData);
          const jobHistory = empData.jobHistory || [];
          setHistory(jobHistory);
          try {
            const delivResult = await deliverablesApi.getAll();
            const delivList = delivResult?.data || delivResult || [];
            const filtered = Array.isArray(delivList)
              ? delivList.filter((d: DeliverableFile) => jobHistory.some((h: JobHistory) => h.jobNo === d.jobNo))
              : [];
            setEmpDeliverables(filtered);
          } catch {
            setEmpDeliverables([]);
          }
        }
      } catch {
        setEmp(null);
      } finally {
        setLoading(false);
      }
    };
    fetchEmployee();
  }, [id]);

  const handlePhotoUpload = async (file: File) => {
    if (!emp) return;
    // Show local preview immediately
    const reader = new FileReader();
    reader.onload = (e) => setPhotoPreview(e.target?.result as string);
    reader.readAsDataURL(file);
    setPhotoUploading(true);
    try {
      const result = await employeesApi.uploadPhoto(String(emp.id), file);
      const photoUrl = result?.photo_url || result?.url || result?.data?.photo_url;
      if (photoUrl) {
        setEmp((prev) => prev ? { ...prev, photo_url: photoUrl } : prev);
        setPhotoPreview(photoUrl);
      }
      setEditSaveMsg({ type: "success", text: "Photo updated successfully" });
      setTimeout(() => setEditSaveMsg(null), 2000);
    } catch {
      // Keep local preview even if upload fails (offline mode)
      setEditSaveMsg({ type: "error", text: "Photo saved locally — will sync when connected" });
      setTimeout(() => setEditSaveMsg(null), 3000);
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!emp) return;
    setSaving(true);
    setEditSaveMsg(null);
    try {
      const payload: Record<string, unknown> = { ...editForm };
      if (editPassword.trim()) {
        payload.password = editPassword.trim();
      }
      await employeesApi.update(String(emp.id), payload);
      setEmp({ ...emp, ...editForm });
      setEditSaveMsg({ type: "success", text: "Profile updated successfully" });
      setTimeout(() => {
        setShowEditModal(false);
        setEditPassword("");
        setShowEditPassword(false);
        setEditSaveMsg(null);
      }, 1200);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to save changes";
      setEditSaveMsg({ type: "error", text: msg });
    } finally {
      setSaving(false);
    }
  };

  if (loading || !emp) {
    return (
      <ERPLayout title="Employee">
        <div className="p-6 space-y-5 animate-pulse">
          <div className="h-8 bg-gray-100 rounded w-64" />
          <div className="grid grid-cols-3 gap-5">
            <div className="col-span-1 space-y-4">
              <div className="bg-white rounded-xl border border-gray-100 p-5 h-64" />
            </div>
            <div className="col-span-2 space-y-4">
              <div className="grid grid-cols-4 gap-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-100 p-4 h-24" />
                ))}
              </div>
            </div>
          </div>
        </div>
      </ERPLayout>
    );
  }

  const dc = deptColors[emp.department] || { bg: "#f3f4f6", text: "#374151" };
  const sc = statusColors[emp.status] || { bg: "#f3f4f6", text: "#374151" };
  const completedJobs = history.filter((h) => h.status === "Completed" || h.status === "Delivered").length;
  const activeJobs = history.filter((h) => h.status === "In Progress").length;

  const fileTypeIcon: Record<string, { icon: string; color: string }> = {
    PDF: { icon: "ri-file-pdf-2-line", color: "#ef4444" },
    DWG: { icon: "ri-file-code-line", color: "#f59e0b" },
    KMZ: { icon: "ri-map-pin-2-line", color: "#10b981" },
    KML: { icon: "ri-map-pin-2-line", color: "#10b981" },
    XLSX: { icon: "ri-file-excel-2-line", color: "#16a34a" },
    ZIP: { icon: "ri-folder-zip-line", color: "#f59e0b" },
    DXF: { icon: "ri-file-code-line", color: "#6366f1" },
    SHP: { icon: "ri-map-2-line", color: "#8b5cf6" },
    JPG: { icon: "ri-image-line", color: "#ec4899" },
  };

  const deliverableStatusColors: Record<string, string> = {
    "Draft": "bg-gray-100 text-gray-600",
    "Under Review": "bg-amber-100 text-amber-700",
    "Approved": "bg-amber-100 text-amber-700",
    "Delivered": "bg-gray-800 text-white",
  };

  return (
    <ERPLayout title={`Employee — ${emp.name}`}>
      <div className="p-6 space-y-5">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <button onClick={() => navigate("/employees")} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 cursor-pointer mt-1">
              <i className="ri-arrow-left-line text-lg" />
            </button>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl font-bold text-gray-900">{emp.name}</h1>
                <span className="text-xs font-mono text-gray-400 bg-gray-100 px-2 py-0.5 rounded">{emp.empId}</span>
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: sc.bg, color: sc.text }}>{emp.status}</span>
              </div>
              <p className="text-sm text-gray-500 mt-0.5">{emp.role} · <span className="font-medium" style={{ color: dc.text }}>{emp.department}</span></p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowEditModal(true)}
              className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-edit-line text-sm" /></span>Edit Profile
            </button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-5">
          {/* Left Column */}
          <div className="col-span-1 space-y-4">
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <div className="flex flex-col items-center text-center mb-5">
                <div className="relative w-20 h-20 mb-3 group">
                  {(emp.photo_url || photoPreview) ? (
                    <img
                      src={photoPreview || emp.photo_url}
                      alt={emp.name}
                      className="w-20 h-20 rounded-full object-cover object-top border-2 border-amber-100"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-amber-500 flex items-center justify-center text-white text-2xl font-bold">
                      {emp.avatar}
                    </div>
                  )}
                  <button
                    onClick={() => photoInputRef.current?.click()}
                    className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                    title="Change photo"
                  >
                    {photoUploading ? (
                      <i className="ri-loader-4-line text-white text-lg animate-spin" />
                    ) : (
                      <i className="ri-camera-line text-white text-lg" />
                    )}
                  </button>
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handlePhotoUpload(file);
                      e.target.value = "";
                    }}
                  />
                </div>
                <h2 className="text-base font-bold text-gray-900">{emp.name}</h2>
                <p className="text-sm text-gray-500">{emp.role}</p>
                <span className="text-xs px-2.5 py-1 rounded-full font-medium mt-2" style={{ background: dc.bg, color: dc.text }}>{emp.department}</span>
              </div>
              <div className="space-y-3 border-t border-gray-50 pt-4">
                {[
                  { icon: "ri-mail-line", label: emp.email },
                  { icon: "ri-phone-line", label: emp.phone },
                  { icon: "ri-briefcase-line", label: emp.type },
                  { icon: "ri-calendar-line", label: `Joined ${emp.joinedDate}` },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-2.5">
                    <div className="w-7 h-7 flex items-center justify-center bg-gray-50 rounded-lg flex-shrink-0">
                      <i className={`${item.icon} text-gray-400 text-sm`} />
                    </div>
                    <span className="text-xs text-gray-600 truncate">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <h3 className="text-sm font-semibold text-gray-800 mb-3">Skills &amp; Expertise</h3>
              <div className="flex flex-wrap gap-2">
                {(emp.skills || []).map((s) => (
                  <span key={s} className="text-xs bg-amber-50 text-amber-700 px-2.5 py-1 rounded-full border border-amber-100 font-medium">{s}</span>
                ))}
                {(!emp.skills || emp.skills.length === 0) && (
                  <span className="text-xs text-gray-400">No skills listed</span>
                )}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <h3 className="text-sm font-semibold text-gray-800 mb-3">Current Workload</h3>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-500">Utilization</span>
                <span className="text-sm font-bold" style={{ color: (emp.utilization || 0) >= 80 ? "#ef4444" : (emp.utilization || 0) >= 60 ? "#f59e0b" : "#10b981" }}>
                  {emp.utilization || 0}%
                </span>
              </div>
              <div className="h-3 bg-gray-100 rounded-full overflow-hidden mb-2">
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${emp.utilization || 0}%`,
                    background: (emp.utilization || 0) >= 80 ? "#ef4444" : (emp.utilization || 0) >= 60 ? "#f59e0b" : "#10b981",
                  }}
                />
              </div>
              <p className="text-xs text-gray-400">
                {(emp.utilization || 0) >= 80 ? "Overloaded — consider redistributing" : (emp.utilization || 0) >= 60 ? "Moderately loaded" : "Available for new assignments"}
              </p>
              <div className="grid grid-cols-2 gap-2 mt-4">
                <div className="bg-amber-50 rounded-lg p-2.5 text-center">
                  <p className="text-lg font-bold text-amber-600">{emp.activeJobs || 0}</p>
                  <p className="text-xs text-amber-500">Active Jobs</p>
                </div>
                <div className="bg-amber-50/60 rounded-lg p-2.5 text-center">
                  <p className="text-lg font-bold text-amber-700">{emp.completedJobs || 0}</p>
                  <p className="text-xs text-amber-600">Completed</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="col-span-2 space-y-4">
            <div className="grid grid-cols-4 gap-3">
              <StatCard label="Total Jobs" value={history.length} icon="ri-file-list-3-line" color="#6366f1" bg="#eef2ff" />
              <StatCard label="Active" value={activeJobs} icon="ri-loader-4-line" color="#f59e0b" bg="#fffbeb" />
              <StatCard label="Completed" value={completedJobs} icon="ri-checkbox-circle-line" color="#10b981" bg="#ecfdf5" />
              <StatCard label="Deliverables" value={empDeliverables.length} icon="ri-folder-line" color="#8b5cf6" bg="#f5f3ff" />
            </div>

            <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
              {(["overview", "jobs", "deliverables"] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer capitalize whitespace-nowrap ${activeTab === tab ? "bg-white text-amber-700 font-semibold" : "text-gray-500 hover:text-gray-700"}`}
                >
                  {tab === "jobs" ? "Job History" : tab === "deliverables" ? `Deliverables (${empDeliverables.length})` : "Overview"}
                </button>
              ))}
            </div>

            {activeTab === "overview" && (
              <div className="space-y-4">
                <div className="bg-white rounded-xl border border-gray-100 p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-800">Utilization Trend</h3>
                      <p className="text-xs text-gray-400 mt-0.5">Last 6 months workload percentage</p>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-amber-400 inline-block" />Normal</span>
                      <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-amber-400 inline-block" />Moderate</span>
                      <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-red-400 inline-block" />Overloaded</span>
                    </div>
                  </div>
                  <UtilizationChart empId={Number(emp.id) || 0} current={emp.utilization || 0} />
                </div>

                <div className="bg-white rounded-xl border border-gray-100 p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-gray-800">Recent Assignments</h3>
                    <button onClick={() => setActiveTab("jobs")} className="text-xs text-amber-600 font-medium hover:text-amber-700 cursor-pointer whitespace-nowrap">
                      View all →
                    </button>
                  </div>
                  {history.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-6">No assignments yet</p>
                  ) : (
                    <div className="space-y-2">
                      {history.slice(0, 3).map((h, i) => (
                        <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${h.status === "Completed" || h.status === "Delivered" ? "bg-amber-500" : h.status === "In Progress" ? "bg-amber-400" : "bg-gray-400"}`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-mono font-bold text-gray-600">{h.jobNo}</span>
                              <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${jobStatusColors[h.status] || "bg-gray-100 text-gray-600"}`}>{h.status}</span>
                            </div>
                            <p className="text-sm text-gray-700 truncate mt-0.5">{h.title}</p>
                          </div>
                          <Link to={`/job-cards/${i + 1}`} className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg cursor-pointer transition-all">
                            <i className="ri-external-link-line text-sm" />
                          </Link>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === "jobs" && (
              <div className="bg-white rounded-xl border border-gray-100 p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-gray-800">Full Job History</h3>
                  <span className="text-xs text-gray-400">{history.length} assignments</span>
                </div>
                {history.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
                      <i className="ri-file-list-3-line text-4xl text-gray-200" />
                    </div>
                    <p className="text-sm text-gray-400">No job assignments found</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {history.map((h, i) => (
                      <div key={i} className="flex items-start gap-4 p-4 rounded-xl border border-gray-100 hover:border-amber-100 hover:bg-amber-50/30 transition-all group">
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${h.status === "Completed" || h.status === "Delivered" ? "bg-amber-100" : h.status === "In Progress" ? "bg-amber-50" : "bg-gray-100"}`}>
                          <i className={`text-sm ${h.status === "Completed" || h.status === "Delivered" ? "ri-checkbox-circle-line text-amber-600" : h.status === "In Progress" ? "ri-loader-4-line text-amber-500" : "ri-time-line text-gray-500"}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className="text-xs font-mono font-bold text-gray-600">{h.jobNo}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${jobStatusColors[h.status] || "bg-gray-100 text-gray-600"}`}>{h.status}</span>
                            <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{h.role}</span>
                          </div>
                          <p className="text-sm font-medium text-gray-800 truncate">{h.title}</p>
                          <div className="flex items-center gap-3 mt-1">
                            <span className="text-xs text-gray-400 flex items-center gap-1">
                              <i className="ri-calendar-line text-xs" />{h.from}
                            </span>
                            <span className="text-gray-300">→</span>
                            <span className="text-xs text-gray-400">{h.to}</span>
                          </div>
                        </div>
                        <Link
                          to={`/job-cards/${i + 1}`}
                          className="w-8 h-8 flex items-center justify-center text-gray-300 group-hover:text-amber-600 group-hover:bg-amber-50 rounded-lg cursor-pointer transition-all opacity-0 group-hover:opacity-100"
                        >
                          <i className="ri-external-link-line text-sm" />
                        </Link>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === "deliverables" && (
              <div className="bg-white rounded-xl border border-gray-100 p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-gray-800">Files Uploaded by {emp.name}</h3>
                  <span className="text-xs text-gray-400">{empDeliverables.length} files</span>
                </div>
                {empDeliverables.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
                      <i className="ri-folder-open-line text-4xl text-gray-200" />
                    </div>
                    <p className="text-sm text-gray-400">No deliverables found for this employee</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {empDeliverables.map((file) => {
                      const ft = fileTypeIcon[file.fileType] || { icon: "ri-file-line", color: "#6b7280" };
                      return (
                        <div key={file.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100 hover:border-gray-200 transition-all group">
                          <div className="w-9 h-9 flex items-center justify-center bg-white rounded-lg border border-gray-100 flex-shrink-0">
                            <i className={`${ft.icon} text-lg`} style={{ color: ft.color }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">{file.fileName}</p>
                            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                              <span className="text-xs text-gray-400">{file.jobNo}</span>
                              <span className="text-gray-300">·</span>
                              <span className="text-xs text-gray-400">{file.size}</span>
                              <span className="text-gray-300">·</span>
                              <span className="text-xs text-gray-400">{file.uploadedAt.split("T")[0]}</span>
                            </div>
                          </div>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${deliverableStatusColors[file.status] || "bg-gray-100 text-gray-600"}`}>
                            {file.status}
                          </span>
                          <button className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg cursor-pointer transition-all opacity-0 group-hover:opacity-100">
                            <i className="ri-download-2-line text-sm" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Profile Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-900">Edit Employee Profile</h3>
              <button onClick={() => { setShowEditModal(false); setEditPassword(""); setShowEditPassword(false); setEditSaveMsg(null); }} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
              {/* Photo Upload in Modal */}
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
                <div className="relative w-14 h-14 group flex-shrink-0">
                  {(emp.photo_url || photoPreview) ? (
                    <img
                      src={photoPreview || emp.photo_url}
                      alt={emp.name}
                      className="w-14 h-14 rounded-full object-cover object-top border-2 border-amber-100"
                    />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-amber-500 flex items-center justify-center text-white text-lg font-bold">
                      {emp.avatar}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => photoInputRef.current?.click()}
                    className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                  >
                    {photoUploading ? (
                      <i className="ri-loader-4-line text-white text-xs animate-spin" />
                    ) : (
                      <i className="ri-camera-line text-white text-xs" />
                    )}
                  </button>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-800">Profile Photo</p>
                  <p className="text-xs text-gray-500 mt-0.5">Upload a photo for this employee</p>
                  <button
                    type="button"
                    onClick={() => photoInputRef.current?.click()}
                    disabled={photoUploading}
                    className="mt-2 flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-600 hover:bg-white hover:border-amber-300 cursor-pointer whitespace-nowrap transition-all disabled:opacity-50"
                  >
                    <span className="w-3 h-3 flex items-center justify-center">
                      <i className={`${photoUploading ? "ri-loader-4-line animate-spin" : "ri-upload-2-line"} text-xs`} />
                    </span>
                    {photoUploading ? "Uploading..." : "Upload Photo"}
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: "Full Name", key: "name" },
                  { label: "Email", key: "email" },
                  { label: "Phone", key: "phone" },
                ].map((f) => (
                  <div key={f.key}>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{f.label}</label>
                    <input
                      type="text"
                      value={(editForm as Record<string, string>)[f.key] || ""}
                      onChange={(e) => setEditForm({ ...editForm, [f.key]: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400"
                    />
                  </div>
                ))}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Role / Designation</label>
                  <select
                    value={editForm.role || ""}
                    onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 cursor-pointer"
                  >
                    <option value="">Select role...</option>
                    {ROLE_OPTIONS.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Department</label>
                  <select
                    value={editForm.department || ""}
                    onChange={(e) => setEditForm({ ...editForm, department: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 cursor-pointer"
                  >
                    {["Field Operations", "Office", "Management", "Lab"].map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                  <select
                    value={editForm.status || ""}
                    onChange={(e) => setEditForm({ ...editForm, status: e.target.value as Employee["status"] })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 cursor-pointer"
                  >
                    {["Active", "On Leave", "Inactive"].map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Change Password Section */}
              <div className="border-t border-gray-100 pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-lock-password-line text-gray-400 text-sm" />
                  </div>
                  <h4 className="text-xs font-semibold text-gray-700">Change Password</h4>
                  <span className="text-xs text-gray-400">— leave blank to keep current</span>
                </div>
                <div className="relative">
                  <input
                    type={showEditPassword ? "text" : "password"}
                    placeholder="Enter new password..."
                    value={editPassword}
                    onChange={(e) => setEditPassword(e.target.value)}
                    className="w-full pr-10 pl-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400"
                  />
                  <button
                    type="button"
                    onClick={() => setShowEditPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                  >
                    <i className={`${showEditPassword ? "ri-eye-off-line" : "ri-eye-line"} text-sm`} />
                  </button>
                </div>
                {editPassword && (() => {
                  const s = getPasswordStrength(editPassword);
                  return (
                    <div className="mt-2">
                      <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-300"
                          style={{ width: s.width, background: s.color }}
                        />
                      </div>
                      <p className="text-xs mt-1 font-medium" style={{ color: s.color }}>{s.label}</p>
                    </div>
                  );
                })()}
              </div>

              {editSaveMsg && (
                <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${
                  editSaveMsg.type === "success"
                    ? "bg-amber-50 text-amber-700 border border-amber-200"
                    : "bg-red-50 text-red-700 border border-red-200"
                }`}>
                  <i className={editSaveMsg.type === "success" ? "ri-checkbox-circle-line" : "ri-error-warning-line"} />
                  {editSaveMsg.text}
                </div>
              )}

              <div className="flex items-center gap-3 pt-2">
                <button
                  onClick={() => { setShowEditModal(false); setEditPassword(""); setShowEditPassword(false); setEditSaveMsg(null); }}
                  className="flex-1 py-2 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={saving}
                  className="flex-1 py-2 bg-amber-500 text-white rounded-xl text-sm font-semibold hover:bg-amber-600 cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </ERPLayout>
  );
}
